import Flex from "common/Boxes/Flex";
import IconWithFallback from "Global/IconWithFallback";
import { ICON_TYPES } from "@shared/common/constants";
import Title from "common/Typography/Title";
import { typography } from "@shared/common/Themes/typography";
import MarginTag from "features/bots/BotForm/General/MarginTag";
import React, { useContext } from "react";
import styled, { useTheme } from "styled-components";
import { BotTemplateData } from "@shared/types";
import Text from "common/Typography/Text";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_VIRTUAL_LIST } from "common/constants";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { isSymbol } from "@shared/types/utils";
import { getIsReverse } from "@shared/features/bots/BotForm/common/utils";
import { getDirectionAndMarginTag } from "@shared/features/bots/utils";

interface PublicBotCardMainInfoProps {
  botData?: BotTemplateData;
  name?: string;
  showSymbol?: boolean;
}

const PublicBotCardMainInfo = ({
  botData: propsBotData,
  name,
  showSymbol,
}: PublicBotCardMainInfoProps) => {
  const { formValues, account, longShort, marginType, multiplier, symbols } =
    useContext(BotFormContext) || {};
  const isMobile = useMedia(
    [`(max-width: ${MOBILE_VIRTUAL_LIST})`],
    [true],
    false
  );

  const getBotData = () => {
    if (propsBotData) return propsBotData;
    else if (formValues) {
      return {
        exchange: isSymbol(account) ? undefined : account?.exchange,
        reverse: getIsReverse(longShort),
        margin_type: isSymbol(marginType) ? undefined : marginType,
        leverage: isSymbol(multiplier) ? undefined : multiplier,
        symbols: isSymbol(symbols) ? undefined : symbols,
      };
    }
  };

  const botData = getBotData();
  const symbol = botData?.symbols?.[0];
  const { isNewLook } = useTheme();

  if (isNewLook) {
    return (
      <Wrapper>
        <IconWithFallback
          iconType={ICON_TYPES.COIN}
          coinOrExchangeName={symbol}
          iconStyle={{ width: 40, height: 40, marginRight: 12 }}
        />
        <div style={{ minWidth: 0 }}>
          <NewLookName title={name}>{name}</NewLookName>
          <NewLookSub>
            {showSymbol && symbol && <span>{symbol}</span>}
            <span>{getDirectionAndMarginTag(botData)}</span>
          </NewLookSub>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <IconWithFallback
        iconType={ICON_TYPES.COIN}
        coinOrExchangeName={symbol}
        iconStyle={{
          width: isMobile ? 36 : 40,
          height: isMobile ? 36 : 40,
          marginRight: isMobile ? 12 : 16,
        }}
      />
      <div style={{ overflow: "hidden" }}>
        <Title
          level={isMobile ? 4 : 3}
          $ellipsis={true}
          $weight={typography.fontWeights.SEMI_BOLD}
          style={{ paddingRight: 8 }}
        >
          {name}
        </Title>
        {showSymbol && (
          <BotSymbolText $isMobile={isMobile}>{symbol}</BotSymbolText>
        )}
        <MarginTag botData={botData} style={{ marginTop: 4 }} />
      </div>
    </Wrapper>
  );
};

export default PublicBotCardMainInfo;

const Wrapper = styled(Flex)`
  justify-content: start;
  overflow: hidden;
`;
const BotSymbolText = styled(Text)<{ $isMobile: boolean }>`
  margin-bottom: 4px;
  ${(p) =>
    p.$isMobile &&
    `
  font-size: ${p.theme.typography.fontSizes.S};
  `}
`;
/* New look: the name may take two lines on narrow cards instead of being
   cut after a few characters. */
const NewLookName = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  padding-right: 8px;
  overflow-wrap: anywhere;
  color: ${(p) => p.theme.colors.titleColor};
  font-size: 15px;
  line-height: 1.35;
  font-weight: ${(p) => p.theme.typography.fontWeights.BOLD};
`;
const NewLookSub = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px 0;
  margin-top: 3px;
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 12.5px;
  line-height: 1.4;
  > span + span::before {
    content: "·";
    margin: 0 6px;
  }
`;
